import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AuthRoute from "../../components/AuthRoute";
import { PAGE_NAME } from "../../types";
import PageLoadingComponent from "../components/PageLoadingComponent";
import Entry from "../pages/Entry";
import HomePage from "../pages/home/HomePage";
import LoginPage from "../pages/login/LoginPage";

// Everything except the entry, login and home is code-split: the first paint
// only ships what a returning user needs to claim their grant.
const EarnPage = lazy(() => import("../pages/earn/EarnPage"));
const ShopPage = lazy(() => import("../pages/shop/ShopPage"));
const InviteUsersPage = lazy(() => import("../pages/inviteUsers/InviteUsersPage"));
const InviteHandler = lazy(() => import("../pages/inviteUsers/InviteHandler"));
const ProfilePage = lazy(() => import("../pages/profile/ProfilePage"));
const HistoryPage = lazy(() => import("../pages/history/HistoryPage"));
const ClaimGrantPage = lazy(() => import("../pages/claimGrant/ClaimGrantPage"));
const DepositPage = lazy(() => import("../pages/depositPage/DepositPage"));
const ProPage = lazy(() => import("../pages/pro/ProPage"));
const ForgePage = lazy(() => import("../pages/forge/ForgePage"));
const MysteryBoxPage = lazy(() => import("../pages/mysteryBox/MysteryBoxPage"));
const StakePage = lazy(() => import("../pages/stake/StakePage"));
const StakeSomePiPage = lazy(() => import("../pages/stakeSomePi/StakeSomePiPage"));
const LuckySpinPage = lazy(() => import("../pages/luckySpin/LuckySpinPage"));
const WatchAndEarnPage = lazy(() => import("../pages/watchAndEarn/WatchAndEarnPage"));
const RewardsPage = lazy(() => import("../pages/rewards/RewardsPage"));
const LeaderboardPage = lazy(() => import("../pages/leaderboard/LeaderboardPage"));
const TapRushGame = lazy(() => import("../pages/games/TapRushGame"));
const MemoryMatchGame = lazy(() => import("../pages/games/MemoryMatchGame"));
const NumberCrunchGame = lazy(() => import("../pages/games/NumberCrunchGame"));
const CryptoQuizGame = lazy(() => import("../pages/games/CryptoQuizGame"));
const TermsOfServicePage = lazy(() => import("../pages/legal/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("../pages/legal/PrivacyPolicyPage"));

const authed = (element: JSX.Element) => <AuthRoute>{element}</AuthRoute>;

export default function RouterPage() {
  return (
    <Suspense fallback={<PageLoadingComponent />}>
      <Routes>
        <Route path="/" element={<Entry />} />
        <Route path={PAGE_NAME.LOGIN} element={<LoginPage />} />
        <Route path={PAGE_NAME.HOME} element={<HomePage />} />
        <Route path="/invite/:code" element={<InviteHandler />} />
        <Route path={PAGE_NAME.TERMS_OF_SERVICE} element={<TermsOfServicePage />} />
        <Route path={PAGE_NAME.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />

        {/* Tabs */}
        <Route path={PAGE_NAME.EARN} element={authed(<EarnPage />)} />
        <Route path={PAGE_NAME.SHOP} element={authed(<ShopPage />)} />
        <Route path={PAGE_NAME.INVITE_USERS} element={authed(<InviteUsersPage />)} />
        <Route path={PAGE_NAME.PROFILE} element={authed(<ProfilePage />)} />

        {/* Mine */}
        <Route path={PAGE_NAME.CLAIM_GRANT} element={authed(<ClaimGrantPage />)} />

        {/* Earn */}
        <Route path={PAGE_NAME.WATCH_AND_EARN} element={authed(<WatchAndEarnPage />)} />
        <Route path={PAGE_NAME.LUCKY_SPIN} element={authed(<LuckySpinPage />)} />
        <Route path={PAGE_NAME.REWARDS} element={authed(<RewardsPage />)} />
        <Route path={PAGE_NAME.LEADERBOARD} element={authed(<LeaderboardPage />)} />
        <Route path={PAGE_NAME.TAP_RUSH} element={authed(<TapRushGame />)} />
        <Route path={PAGE_NAME.MEMORY_MATCH} element={authed(<MemoryMatchGame />)} />
        <Route path={PAGE_NAME.NUMBER_CRUNCH} element={authed(<NumberCrunchGame />)} />
        <Route path={PAGE_NAME.CRYPTO_QUIZ} element={authed(<CryptoQuizGame />)} />

        {/* Shop */}
        <Route path={PAGE_NAME.DEPOSIT} element={authed(<DepositPage />)} />
        <Route path={PAGE_NAME.PRO} element={authed(<ProPage />)} />
        <Route path={PAGE_NAME.FORGE} element={authed(<ForgePage />)} />
        <Route path={PAGE_NAME.MYSTERY_BOX} element={authed(<MysteryBoxPage />)} />
        <Route path={PAGE_NAME.STAKE} element={authed(<StakePage />)} />
        <Route path={PAGE_NAME.STAKE_SOME_PI} element={authed(<StakeSomePiPage />)} />

        {/* Profile */}
        <Route path={PAGE_NAME.HYSTORY_PAGE} element={authed(<HistoryPage />)} />

        {/* Old URLs still in users' history / bookmarks */}
        <Route path={PAGE_NAME.GAMES} element={<Navigate to={PAGE_NAME.EARN} replace />} />
        <Route path={PAGE_NAME.TREASURE_VAULT} element={<Navigate to={PAGE_NAME.SHOP} replace />} />
        <Route path={PAGE_NAME.WALLET} element={<Navigate to={PAGE_NAME.PROFILE} replace />} />
        <Route path={PAGE_NAME.SETTINGS} element={<Navigate to={PAGE_NAME.PROFILE} replace />} />
        <Route path={PAGE_NAME.REFERRALS} element={<Navigate to={PAGE_NAME.INVITE_USERS} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
